import React, { useState } from "react";
import api from "../api/client";
import { formatDateTime } from "../utils/datetime";
import { useSort, sortRows, SortableTh } from "./SortableTh";

/* Live Windows Event Viewer for a device. Queries actual recent events on
 * demand (agent query_event_log command) rather than the background summary
 * counts, so a tech can triage errors without RDP. */

type Ev = { time: string; level: string; source: string; id: number; message: string };

const LOGS = ["System", "Application", "Security", "Setup"];
const LEVELS = ["Critical", "Error", "Warning", "Information", "All"];
const WINDOWS = [
  { label: "Last 1 hour", hours: 1 },
  { label: "Last 24 hours", hours: 24 },
  { label: "Last 7 days", hours: 168 },
  { label: "Last 30 days", hours: 720 },
];

function levelClass(level: string): string {
  const l = (level || "").toLowerCase();
  if (l.startsWith("crit")) return "bg-red-200 text-red-900 dark:bg-red-900/60 dark:text-red-200";
  if (l.startsWith("err")) return "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300";
  if (l.startsWith("warn")) return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
}

export default function EventLogViewer({ deviceId, online }: { deviceId: number; online: boolean }) {
  const [log, setLog] = useState("System");
  const [level, setLevel] = useState("Error");
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [events, setEvents] = useState<Ev[] | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const sort = useSort("time", "desc");

  const load = async () => {
    setLoading(true);
    setError("");
    setExpanded(null);
    try {
      const r = await api.post(`/api/v1/devices/${deviceId}/event-log/query`, {
        log,
        level,
        hours,
        max: 300,
      });
      const cid = r.data.command_id;
      for (let i = 0; i < 25; i++) {
        await new Promise((res) => setTimeout(res, 1500));
        const s = await api.get(`/api/v1/devices/${deviceId}/command/${cid}`);
        if (s.data.status === "completed") {
          const res = s.data.result || {};
          setEvents(res.events || []);
          if (res.error) setError(`Log note: ${res.error}`);
          setLoading(false);
          return;
        }
        if (s.data.status === "failed") {
          setError(s.data.error_message || "Query failed.");
          setLoading(false);
          return;
        }
      }
      setError("Timed out waiting for the device.");
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed to query the event log.");
    } finally {
      setLoading(false);
    }
  };

  const shown = (events || []).filter((e) => {
    const q = search.trim().toLowerCase();
    return !q || e.source?.toLowerCase().includes(q) || e.message?.toLowerCase().includes(q) || String(e.id).includes(q);
  });

  return (
    <div className="space-y-3">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Log</label>
            <select
              value={log}
              onChange={(e) => setLog(e.target.value)}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
            >
              {LOGS.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Minimum level</label>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
            >
              {LEVELS.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Time window</label>
            <select
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
            >
              {WINDOWS.map((w) => (
                <option key={w.hours} value={w.hours}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={load}
            disabled={loading || !online}
            className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            title={online ? "Query the device now" : "Device is offline"}
          >
            {loading ? "Querying…" : "Load events"}
          </button>
          {events && (
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter shown events…"
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm ml-auto w-56"
            />
          )}
        </div>
        {!online && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
            The device is offline — bring it online to query its event log.
          </p>
        )}
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      {events && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
            {shown.length} event{shown.length === 1 ? "" : "s"} {log} · {level.toLowerCase()}+
          </div>
          {shown.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 p-6 text-center">
              No matching events. 🎉
            </p>
          ) : (
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <SortableTh label="Time" sortKey="time" sort={sort.sort} onSort={sort.toggle} />
                  <SortableTh label="Level" sortKey="level" sort={sort.sort} onSort={sort.toggle} />
                  <SortableTh label="Source" sortKey="source" sort={sort.sort} onSort={sort.toggle} />
                  <SortableTh label="Event ID" sortKey="id" sort={sort.sort} onSort={sort.toggle} />
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-gray-700 dark:text-gray-300">
                {sortRows(shown, sort.sort, (e, key) => {
                  switch (key) {
                    case "time": return e.time;
                    case "id": return e.id;
                    case "source": return (e.source || "").toLowerCase();
                    default: return (e.level || "").toLowerCase();
                  }
                }).map((e, i) => (
                  <tr
                    key={i}
                    className="align-top cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30"
                    onClick={() => setExpanded(expanded === i ? null : i)}
                  >
                    <td className="px-3 py-1.5 whitespace-nowrap">{formatDateTime(e.time)}</td>
                    <td className="px-3 py-1.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${levelClass(e.level)}`}>
                        {e.level || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 truncate max-w-[12rem]" title={e.source}>{e.source}</td>
                    <td className="px-3 py-1.5 font-mono">{e.id}</td>
                    <td className="px-3 py-1.5">
                      <div className={expanded === i ? "whitespace-pre-wrap" : "truncate max-w-[28rem]"}>
                        {e.message || "—"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!events && !loading && (
        <p className="text-sm text-gray-500 dark:text-gray-400 p-6 text-center">
          Pick a log, level, and time window, then <strong>Load events</strong> to pull live entries from the device.
        </p>
      )}
    </div>
  );
}
